/**
 * A rAF-throttled "schedule" primitive: `schedule` cancels any frame still pending from a previous call
 * before requesting a new one, so a burst of events (e.g. `scroll`) collapses into at most one callback
 * per frame. Shared by SectionScrollBottomHook and StickyTableHeaderSyncHook, which each throttle a
 * `scroll` listener this way but differ in what they attach it to (a plain container listener vs. a
 * capture-phase root listener) and in what the deferred callback does (an argument-less recheck vs. a
 * per-event DOM write) -- so only this cancel-then-schedule idiom is shared, not the listener itself.
 * Pattern origin: `src/containers/Chat/hooks/useScrollToBottom.ts`.
 *
 * @returns `schedule` -- call with the work to run on the next animation frame; `cancel` -- call from the
 * effect's cleanup to drop any frame still pending after the listener is removed.
 * @example
 * const { schedule, cancel } = createRafThrottle();
 * el.addEventListener("scroll", () => schedule(doWork));
 * // on cleanup:
 * cancel();
 */
export const createRafThrottle = (): { schedule: (callback: () => void) => void; cancel: () => void } => {
  let rafId = 0;
  return {
    schedule: (callback: () => void) => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(callback);
    },
    cancel: () => cancelAnimationFrame(rafId),
  };
};

export default createRafThrottle;
